import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { pusherConfig, isRealtimeRouteEnabled } from '../config/pusher';
import { StatusSession } from '../constants';
import type { PanelStore } from '../store/interfaces';
import { clearChannelRegistry } from './channel-registry';
import {
  bindPusherConnectionState,
  disconnectPusherClient,
  getInitialPusherConnectionState,
  getPusherClient,
} from './pusher-client';
import type { RealtimeConnectionState, RealtimeContextValue } from './types';

interface RealtimeProviderProps {
  children: ReactNode;
}

export const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export const RealtimeProvider = ({ children }: RealtimeProviderProps) => {
  const { pathname } = useLocation();
  const sessionStatus = useSelector((state: PanelStore.RootState) => state.auth.sessionStatus);
  const token = useSelector((state: PanelStore.RootState) => state.auth.token);
  const [connectionState, setConnectionState] = useState<RealtimeConnectionState>('idle');
  const [client, setClient] = useState(() => getPusherClient());

  const isEnabledOnRoute = isRealtimeRouteEnabled(pathname);
  const isValidSession = sessionStatus === StatusSession.LOGGED_IN && Boolean(token);
  const shouldConnect = pusherConfig.enabled && isEnabledOnRoute && isValidSession;

  const disconnect = useCallback(() => {
    const existingClient = getPusherClient();
    if (existingClient) {
      clearChannelRegistry(existingClient);
    }

    disconnectPusherClient();
    setClient(null);
    setConnectionState('disconnected');
  }, []);

  const connect = useCallback(() => {
    if (!shouldConnect) {
      disconnect();
      return;
    }

    const nextClient = getPusherClient();
    setClient(nextClient);

    if (!nextClient) {
      setConnectionState('idle');
      return;
    }

    setConnectionState(getInitialPusherConnectionState());
  }, [disconnect, shouldConnect]);

  useEffect(() => {
    connect();
  }, [connect]);

  useEffect(() => {
    if (!client) {
      return undefined;
    }

    const unbindStateChange = bindPusherConnectionState(setConnectionState);
    return () => {
      if (unbindStateChange) {
        unbindStateChange();
      }
    };
  }, [client]);

  useEffect(
    () => () => {
      disconnect();
    },
    [disconnect]
  );

  const contextValue = useMemo<RealtimeContextValue>(
    () => ({
      client,
      isConfigured: pusherConfig.enabled,
      isEnabledOnRoute,
      isConnected: connectionState === 'connected',
      connectionState,
      connect,
      disconnect,
    }),
    [client, isEnabledOnRoute, connectionState, connect, disconnect]
  );

  return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
};
